import { useCallback, useState } from 'react';
import { qcErrorMessage } from '../../../services/qcApiError';
import { qualityCheckServices, QcVisitStateDto } from '../../../services/qualityCheck';
import { QcRequestState } from './qcRequestState';
import { ReviewImage, correctedIdentifiable } from './imageMeta';

/**
 * SKIN-243 — the correction of one photograph's identifiable flag, as a unit.
 *
 * Extracted from `VisitDetail` rather than left inline: the container already owns the
 * visit read, the per-image decrypt, the assessments, the lesion feedback and the
 * approval, and adding a sixth concern inline pushed it past the size its own task spec
 * had drawn a line at. This is the smallest piece that comes out whole — two pieces of
 * state and one request that only ever talk to each other.
 *
 * It deliberately does **not** own the visit payload. On success it hands the caller the
 * freshly read state and lets the container apply it, so there is still exactly one place
 * that decides what a loaded visit means (`applyVisit`).
 */
export function useIdentifiableCorrection(args: {
    uuidInvestigation: string | undefined;
    uuidVisit: string | undefined;
    /** The live list, so the open dialog follows the card rather than a captured copy. */
    images: ReviewImage[];
    /**
     * The flag as written, reported the moment the server accepts it — before the re-read,
     * which may not arrive. Without it a failed re-read leaves the card showing the old
     * value after a correction that landed, and the reviewer corrects it a second time.
     */
    onWritten: (encryptedFileUuid: string, identifiable: boolean) => void;
    /** Hand the re-read visit to the container, which owns what a loaded visit means. */
    onCorrected: (payload: QcVisitStateDto) => void;
}): {
    /** The photograph under correction, or `null` when the dialog is closed. */
    correctingImage: ReviewImage | null;
    request: QcRequestState;
    open: (encryptedFileUuid: string) => void;
    close: () => void;
    confirm: (reason: string) => Promise<void>;
} {
    const { uuidInvestigation, uuidVisit, images, onWritten, onCorrected } = args;

    /**
     * The uuid alone, not the image: the flag is re-read from the live list on every
     * render, so the dialog cannot end up stating a direction the card has stopped showing.
     */
    const [correcting, setCorrecting] = useState<string | null>(null);
    const [request, setRequest] = useState<QcRequestState>({ status: 'editable' });

    const correctingImage = images.find((image) => image.encryptedFileUuid === correcting) ?? null;

    const close = useCallback(() => {
        setCorrecting(null);
        setRequest({ status: 'editable' });
    }, []);

    const confirm = useCallback(async (reason: string) => {
        if (!uuidInvestigation || !uuidVisit || !correctingImage) return;
        setRequest({ status: 'submitting' });

        /**
         * The write, alone in its own try.
         *
         * It used to share one with the re-read below, and that is a worse bug than it
         * looks: a re-read that failed after a correction had already been written told the
         * reviewer "the correction could not be saved" and re-armed the button, so the
         * obvious next move — press it again — appended a **second** entry to a permanent
         * audit trail about a patient's photograph. Only the step that actually failed may
         * claim the failure.
         */
        const identifiable = correctedIdentifiable(correctingImage.identifiable);
        try {
            await qualityCheckServices.correctIdentifiable(
                uuidInvestigation,
                uuidVisit,
                correctingImage.encryptedFileUuid,
                { identifiable, reason },
            );
        } catch (err: any) {
            // The dialog stays open holding the reason: nothing was written, and the
            // reviewer's justification is the expensive part to retype.
            setRequest({
                status: 'failed',
                error: qcErrorMessage(err, null),
            });
            return;
        }

        // Written. The card takes the new flag now rather than on the re-read, so the screen
        // is correct even if the read below never lands; the dialog has nothing left to hold.
        onWritten(correctingImage.encryptedFileUuid, identifiable);
        setCorrecting(null);
        setRequest({ status: 'editable' });

        /**
         * Re-read rather than patch in place. The audit line names the reviewer, and the
         * correction endpoint answers with an actor uuid — only the visit read resolves it
         * to a name. Building that line from the session would be the client asserting who
         * acted on a patient's photograph, which is exactly the claim the audit trail exists
         * to make on the server's authority.
         *
         * Its failure is a stale screen, not a failed correction: the flag is changed on the
         * server either way, and the reviewer sees the old value until the next load. Logged
         * the way the assessment upserts log theirs, until this feature has a toast.
         */
        try {
            onCorrected(await qualityCheckServices.getVisitQcState(uuidInvestigation, uuidVisit));
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error(
                'QC correction saved, but the visit could not be re-read',
                qcErrorMessage(err, 'QC identifiable correction failed'),
            );
        }
    }, [uuidInvestigation, uuidVisit, correctingImage, onWritten, onCorrected]);

    return { correctingImage, request, open: setCorrecting, close, confirm };
}
